import type { StatsAggVarDto } from '../types/adminAssessment';
import type { DataExportVarDto } from '../types/api/stats';
import type { ExportTable } from './exportTable';

export interface DataExportVarInfo {
  /** The full MVar (`tablename.mvarname`) — unique selector id, and the request var Name. */
  id: string;
  /** DName — the option text in Setup and the Variable column of the data map. */
  label: string;
  /** The mvarname with the table name stripped — the MVar column of the data map. */
  name: string;
  questionText: string;
  labels: Array<{ dataValue: string; dataLabel: string }>;
}

export const DATA_MAP_COLUMNS = ['Variable', 'MVar', 'Question', 'Answers'];

/** `tablename.mvarname` → `mvarname`. */
export const stripTableName = (mVar: string): string => {
  const index = mVar.indexOf('.');
  return index < 0 ? mVar : mVar.slice(index + 1);
};

const ENTITIES: Record<string, string> = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'" };

/** Question text may be authored HTML — flatten it to plain text for lists and files. */
export const htmlToPlainText = (html: string | undefined): string =>
  (html ?? '')
    .replace(/<\/?(?:br|p|div|li|ul|ol|tr|td|h[1-6])\b[^>]*>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&(nbsp|amp|lt|gt|quot|#39);/g, (_m, key: string) => ENTITIES[key])
    .replace(/\s+/g, ' ')
    .trim();

/** Variables for the Setup selector, from AdminAssessmentDto.aggVars (de-duplicated by MVar). */
export function buildVarInfos(aggVars: StatsAggVarDto[]): DataExportVarInfo[] {
  const seen = new Set<string>();
  const result: DataExportVarInfo[] = [];
  aggVars.forEach((v) => {
    const mVar = (v.mVar ?? '').trim();
    if (!mVar || seen.has(mVar)) {
      return;
    }
    seen.add(mVar);
    const dName = (v.dName ?? '').trim();
    const name = stripTableName(mVar);
    result.push({
      id: mVar,
      label: dName || name,
      name,
      questionText: htmlToPlainText(v.qText),
      labels: (v.dataLabels ?? []).map((l) => ({ dataValue: l.dataValue, dataLabel: l.dataLabel })),
    });
  });
  return result;
}

/** Data map for the given variable ids, in order: question text and `value=label` answers. */
export function buildDataMapTable(selectedIds: string[], vars: DataExportVarInfo[]): ExportTable {
  const byId = new Map(vars.map((v) => [v.id, v]));
  const rows: string[][] = [];
  selectedIds.forEach((id) => {
    const info = byId.get(id);
    if (info) {
      rows.push([info.label, info.name, info.questionText, info.labels.map((l) => `${l.dataValue}=${l.dataLabel}`).join(' | ')]);
    }
  });
  return { columns: DATA_MAP_COLUMNS, rows };
}

/**
 * The request's vars: the selected variables in selection order, or every aggVar when none are
 * selected. Name is the full `tablename.mvarname` as defined on the aggVar; Segment is unused.
 */
export function resolveExportVars(selectedIds: string[], aggVars: StatsAggVarDto[]): DataExportVarDto[] {
  const ids = selectedIds.length > 0 ? selectedIds : buildVarInfos(aggVars).map((v) => v.id);
  return ids.map((id) => ({ Name: id, Segment: '' }));
}
